/**
 * Global tooltip configuration — THE single source of truth for every
 * tooltip in the suite (hub, panel shell, document workspace, register apps).
 *
 * Timing/behavior lives here: change one field and every mounted tooltip
 * picks it up on the next render, live. Appearance lives in the served
 * `suite-ui.css` (`--tooltip-*` token family + the `.suite-ui-tooltip`
 * surface class, measured against pik's tooltip), so one stylesheet edit
 * retunes the look suite-wide. Per-tooltip props still win where a call
 * site passes them explicitly; the central values are only the defaults.
 */

import { useSyncExternalStore } from 'react';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipSystemConfig {
    /** Hover/focus delay before a tooltip opens (ms). */
    delayDuration: number;
    /** Grace period after the pointer leaves before the tooltip closes
        (ms); also the window that lets the pointer cross the side gap
        onto the content without it snapping shut. */
    closeDelayDuration: number;
    /** When a second tooltip opens within this many ms of the previous
        one closing, it skips the open delay (pointer traveling across a
        toolbar of adjacent triggers feels instant). */
    skipDelayDuration: number;
    /** Default side when a tooltip does not ask for one. */
    side: TooltipSide;
    /** Default trigger→content gap in px. */
    sideOffset: number;
    /** Kill switch: when true no tooltip in the suite opens. */
    disabled: boolean;
}

export const DEFAULT_TOOLTIP_CONFIG: TooltipSystemConfig = {
    // 700ms — the radix default the panel shell has always used; pik's
    // toolbar feel. Browser-default tooltips feel faster but flash open
    // on incidental pointer crossings.
    delayDuration: 700,
    closeDelayDuration: 300,
    skipDelayDuration: 300,
    side: 'top',
    sideOffset: 4,
    disabled: false,
};

let config: TooltipSystemConfig = { ...DEFAULT_TOOLTIP_CONFIG };

const listeners = new Set<() => void>();

export function getTooltipConfig(): TooltipSystemConfig {
    return config;
}

/** Merge a patch into the central config and notify every mounted tooltip. */
export function setTooltipConfig(patch: Partial<TooltipSystemConfig>): void {
    config = { ...config, ...patch };
    listeners.forEach((listener) => listener());
}

export function resetTooltipConfig(): void {
    setTooltipConfig(DEFAULT_TOOLTIP_CONFIG);
}

export function subscribeTooltipConfig(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** React binding: every tooltip re-renders the moment the config changes. */
export function useTooltipSystemConfig(): TooltipSystemConfig {
    return useSyncExternalStore(subscribeTooltipConfig, getTooltipConfig);
}
